import moment from 'moment';
import { LONG_TIME_NO_SEE_THRESHOLD, POLICY_DUE_SOON_STAT_THRESHOLD } from '../../../constants/client-queue';
import { adviserFactsheetOf, qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import { clientNameOf, familiesOf, isRecruitmentOf, trackingOf } from './clientPresentation';
import type { PolicyDueEntry, QueueClient } from '../../../types/client';

// List-level derivations for the ClientV2 page: the 4 stat tiles, the tab-row
// filter/counts, and the sort. Kept separate from clientPresentation (per-client
// chips) because these operate over the WHOLE array. CRITICAL (handoff PART 6c):
// the stat tiles and the today-queue always read the full client array - only
// the list render is ever paginated (Slice 6). These helpers take the full
// array by design; never pass a paged slice.

const potentialLevelOf = (client: QueueClient): number => {
  const level = Number.parseInt(String(client.scoreLevel), 10);
  return client.validScore && !Number.isNaN(level) ? level : 99;
};

const isHighPotential = (client: QueueClient): boolean => potentialLevelOf(client) === 0;

// Same identity rule the rows render, so 姓名 A–Z orders by what is on screen.
const displayNameOf = (client: QueueClient): string => clientNameOf(client);

// dob month === reference month, ignoring day/year - "本月生日" counts everyone
// whose birthday lands in the current calendar month, whether it has passed or
// is still upcoming (unlike the queue's BIRTHDAY_THIS_MONTH, which only surfaces
// upcoming ones). The dob is adviser-owned factsheet data, so it is read
const birthdayThisMonth = (client: QueueClient, now: moment.Moment, adviserEmail?: string): boolean => {
  const dob = adviserFactsheetOf(client, adviserEmail)?.dob || client.dob;
  if (!dob) return false;
  const parsed = moment.parseZone(dob);
  return parsed.isValid() && parsed.month() === now.month();
};

const longTimeNoSee = (client: QueueClient, now: moment.Moment, adviserEmail?: string): boolean => {
  const date = qualifyingFactsheetOf(client, adviserEmail)?.latestMeetingDate;
  if (!date) return false;
  const lastMeeting = moment(date).startOf('day');
  const cutoff = now.clone().startOf('day').subtract(LONG_TIME_NO_SEE_THRESHOLD);
  return lastMeeting.isValid() && lastMeeting.isBefore(cutoff);
};

/**
 * Soonest due date per client (mirrors buildTodayQueue's policyDueById dedupe),
 * kept only when it falls in `[today, today + POLICY_DUE_SOON_STAT_THRESHOLD]`.
 *
 * Returns the dates, not just the ids, because the list this tile opens is
 * ordered by them — the adviser's next renewal conversation is the first row.
 */
const policyDueSoonDates = (policyDue: PolicyDueEntry[], now: moment.Moment): Map<string, number> => {
  const startOfToday = now.clone().startOf('day');
  const windowEnd = startOfToday.clone().add(POLICY_DUE_SOON_STAT_THRESHOLD);
  const soonestById = new Map<string, moment.Moment>();
  (policyDue || []).forEach(entry => {
    if (!entry || !entry._id || !entry.policyDueDate) return;
    const due = moment(entry.policyDueDate).startOf('day');
    if (!due.isValid()) return;
    const existing = soonestById.get(entry._id);
    if (!existing || due.isBefore(existing)) {
      soonestById.set(entry._id, due);
    }
  });
  const dueSoon = new Map<string, number>();
  soonestById.forEach((due, id) => {
    if (!due.isBefore(startOfToday) && !due.isAfter(windowEnd)) {
      dueSoon.set(id, due.valueOf());
    }
  });
  return dueSoon;
};

export const CLIENT_STAT_KEYS = ['highPotential', 'birthdayThisMonth', 'policyDueSoon', 'longTimeNoSee'] as const;
export type ClientStatKey = (typeof CLIENT_STAT_KEYS)[number];
export type ClientStats = Record<ClientStatKey, number>;

export type ClientStatsInput = {
  policyDue?: PolicyDueEntry[];
  now?: moment.MomentInput;
  adviserEmail?: string;
};

/**
 * One predicate per tile, built once from the same inputs. Both the figure a
 * tile shows and the list it opens go through here, which is the point: a tile
 * reading 「11 名」 that filtered on a separately-written rule would sooner or
 * later open a list of some other length, and the number on screen would be a
 * lie with nothing to catch it.
 *
 * `policyDueSoonAt` rides along for the same reason — the one tile that orders
 * its list reads the very dates its predicate was built from.
 */
const statContextOf = (
  input: ClientStatsInput
): {
  predicates: Record<ClientStatKey, (client: QueueClient) => boolean>;
  policyDueSoonAt: Map<string, number>;
} => {
  const now = moment(input.now);
  const policyDueSoonAt = policyDueSoonDates(input.policyDue || [], now);
  return {
    policyDueSoonAt,
    predicates: {
      highPotential: isHighPotential,
      birthdayThisMonth: client => birthdayThisMonth(client, now, input.adviserEmail),
      policyDueSoon: client => policyDueSoonAt.has(client._id),
      longTimeNoSee: client => longTimeNoSee(client, now, input.adviserEmail)
    }
  };
};

// The 4 stat tiles (handoff PART 6c): 高潛力客戶 / 本月生日 / 60天續期 (unit 名,
// client count not policy count) / 逾 1 年未見. Always over the full array.
export const clientStatsOf = (clients: QueueClient[], input: ClientStatsInput = {}): ClientStats => {
  const { predicates } = statContextOf(input);
  return CLIENT_STAT_KEYS.reduce((stats, key) => {
    stats[key] = clients.filter(predicates[key]).length;
    return stats;
  }, {} as ClientStats);
};

/**
 * Narrows the list to the clients behind one stat tile — the tiles are the only
 * figures on the page that were not reachable as a list, so tapping one now
 * answers 「which 11 clients?」. `null` (no tile active) leaves the list alone.
 *
 * Composes with the tab row, the sidebar and the search box rather than
 * replacing them, so the tile is a narrowing like any other; the tile counts
 * themselves keep reading the full array (PART 6c) and do not move as it is
 * applied.
 *
 * 60天續期 is the one tile whose members carry a deadline, so it also orders
 * them — soonest renewal first, which is the order the adviser has to work in.
 * The other three keep the incoming (server) order. This survives to the screen
 * because the `default` sort is a deliberate no-op (see `sortClients`); an
 * adviser who picks 姓名 A–Z or 潛力高→低 from the menu, or who is on the 提醒
 * tab (always ordered by reminder date), overrides it — an explicit choice
 * should win over a default.
 */
export const filterClientsByStat = (
  clients: QueueClient[],
  stat: ClientStatKey | null,
  input: ClientStatsInput = {}
): QueueClient[] => {
  if (!stat) return clients;
  const { predicates, policyDueSoonAt } = statContextOf(input);
  // `.filter` already hands back a fresh array, so sorting it in place is safe.
  const narrowed = clients.filter(predicates[stat]);
  if (stat !== 'policyDueSoon') return narrowed;
  // Every member has an entry by construction — the predicate IS the map lookup
  // — so the fallback only guards the type. Ties keep the incoming order.
  const dueAt = (client: QueueClient): number => policyDueSoonAt.get(client._id) ?? Number.POSITIVE_INFINITY;
  return narrowed.sort((left, right) => dueAt(left) - dueAt(right));
};

export const CLIENT_TABS = ['all', 'follow', 'potential', 'family', 'reminder', 'recruit'] as const;
export type ClientTab = (typeof CLIENT_TABS)[number];

/**
 * The tabs rendered as chips in the row. 家庭 is a tab like any other — it is
 * just reached from the sort/state menu instead, because the sidebar's
 * 家庭/家族樹 section already narrows by a NAMED family and this tab only asks
 * 「has any family」; keeping both as top-level chips spent row width on the
 * weaker of the two. Everything else about it is unchanged.
 *
 * (The 自定 tab that used to sit here was deleted outright: its predicate was
 * `() => true`, it carried no count, and the sidebar it was a placeholder for
 * has shipped.)
 */
export const CLIENT_MENU_TABS = ['family'] as const;

/**
 * Row order, which is deliberately NOT CLIENT_TABS' order: 招募 leads, then 全部
 * and the rest as declared. Recruitment is the one tab whose list is worked
 * rather than browsed — a standing shortlist of a handful of people, next to
 * four tabs that all answer 「show me some of my 185 clients」 — and it was
 * sitting at the far end of the row, which is the corner a phone crops first.
 *
 * The filter stays so a tab that moves into the menu cannot also appear here,
 * and so a newly declared tab still shows up without touching this line. It
 * does not cover 招募 itself: that one is prepended unconditionally, ahead of
 * the filter, so if it ever joins CLIENT_MENU_TABS this line has to be changed
 * by hand or the tab would render in both places.
 */
export const CLIENT_TAB_ROW: readonly ClientTab[] = [
  'recruit',
  ...CLIENT_TABS.filter(tab => tab !== 'recruit' && !(CLIENT_MENU_TABS as readonly string[]).includes(tab))
];

// Predicate per tab.
const TAB_PREDICATE: Record<ClientTab, (client: QueueClient, adviserEmail?: string) => boolean> = {
  all: () => true,
  follow: client => trackingOf(client) !== null,
  potential: client => isHighPotential(client),
  family: (client, adviserEmail) => familiesOf(client, adviserEmail).length > 0,
  reminder: (client, adviserEmail) => {
    const factsheet = qualifyingFactsheetOf(client, adviserEmail);
    return Boolean(factsheet?.fsReminderActive && factsheet.fsReminderDate);
  },
  recruit: (client, adviserEmail) => isRecruitmentOf(client, adviserEmail)
};

export const filterClientsByTab = (clients: QueueClient[], tab: ClientTab, adviserEmail?: string): QueueClient[] =>
  clients.filter(client => TAB_PREDICATE[tab](client, adviserEmail));

// Counts shown on each tab chip. `all` is the full length. Every tab is counted,
// including the menu-only ones — the menu does not render a count today, but the
// count is what the caller would need the moment it does.
export const tabCountsOf = (clients: QueueClient[], adviserEmail?: string): Partial<Record<ClientTab, number>> => {
  const counts: Partial<Record<ClientTab, number>> = {};
  CLIENT_TABS.forEach(tab => {
    counts[tab] = filterClientsByTab(clients, tab, adviserEmail).length;
  });
  return counts;
};

/**
 * The adviser-level client-base level, an average-CPI band the server computes
 * across the WHOLE client list (utils/client.js getClientBaseLevel) and then
 * piggybacks on `clients[0]`.
 *
 * Reading index 0 the way V1 does (clientPage.js:1190) is fragile: a
 * GET_CLIENT_BY_ID refresh replaces that client's object with a payload that
 * has no `clientBaseLevel`, and a newly created client is prepended in front of
 * it — either way V1's indicator silently goes grey until the next full fetch.
 * Search the array instead; nothing else carries the field.
 */
export const clientBaseLevelOf = (clients: QueueClient[]): number | null => {
  const carrier = (clients || []).find(client => typeof client.clientBaseLevel === 'number');
  return carrier ? (carrier.clientBaseLevel as number) : null;
};

// `default` is V1's unsorted list: clientState '' / 'all' applies no client-side
// sort at all, so the order is the one the server sent — `clientController`'s
// getClient sorts by `createdate` DESCENDING (clientController.js:381-385), i.e.
// the newest client first. The two real sorts stay opt-in, exactly as V1's
// <select> makes them (clientPage.js:2176-2177).
export const CLIENT_SORTS = ['default', 'potential', 'name'] as const;
export type ClientSort = (typeof CLIENT_SORTS)[number];

// A stable, non-mutating sort. 潛力高→低 orders by CPI level ascending (0 = high
// first), invalid scores last; ties and the 姓名 A–Z sort fall back to a
// locale-aware name compare so the order is deterministic.
export const sortClients = (
  clients: QueueClient[],
  sort: ClientSort,
  adviserEmail?: string,
  tab?: ClientTab
): QueueClient[] => {
  // Server order, untouched — the caller already holds a filtered copy, and
  // reordering it here is precisely what `default` must not do.
  if (sort === 'default' && tab !== 'reminder') return clients;

  const byName = (left: QueueClient, right: QueueClient): number =>
    displayNameOf(left).localeCompare(displayNameOf(right));
  return [...clients].sort((left, right) => {
    // V1's allReminder view always sorted active reminders by their due date.
    // Keep that behavior when the v46 reminder tab is active; the normal sort
    // toggle resumes as soon as the adviser switches to another tab.
    if (tab === 'reminder') {
      const leftDate = qualifyingFactsheetOf(left, adviserEmail)?.fsReminderDate;
      const rightDate = qualifyingFactsheetOf(right, adviserEmail)?.fsReminderDate;
      const byReminder = moment(leftDate).valueOf() - moment(rightDate).valueOf();
      return byReminder !== 0 ? byReminder : byName(left, right);
    }
    if (sort === 'name') return byName(left, right);
    const byPotential = potentialLevelOf(left) - potentialLevelOf(right);
    return byPotential !== 0 ? byPotential : byName(left, right);
  });
};
